import { Box, Group, Stack, Text } from '@mantine/core';
import { useLayoutEffect, type ReactNode } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import type { Crumb } from '../Header/headerCrumbs.js';
import { useSetPageHeader } from '../PageHeader/PageHeaderContext.js';

export type { Crumb };

/**
 * Every page body. Above `sm` the title, count, trail and actions are published to the shell's
 * header; below it the header takes the title only and the count and actions render at the top
 * of the body. There is no `description`: `count` is the fact line, and facts change.
 */
export const PageContainer = ({
    title,
    count,
    breadcrumbs,
    actions,
    children,
}: {
    title: string;
    count?: ReactNode;
    breadcrumbs?: Crumb[];
    actions?: ReactNode;
    children: ReactNode;
}) => {
    const isMobile = useIsMobile();
    const setHeader = useSetPageHeader();
    const crumbsKey = JSON.stringify(breadcrumbs ?? null);

    // `useLayoutEffect`, not `useEffect`: the sibling `Header` reads this published state, and
    // publishing synchronously before paint is what stops a route change from flashing one frame
    // of the previous page's stale header. The pages' own `useMemo`'d `actions` (so this effect's
    // deps don't change on every unrelated render) is what removes the per-keystroke re-publish —
    // the effect timing itself is not the perf problem.
    useLayoutEffect(() => {
        setHeader({ title, count: isMobile ? undefined : count, breadcrumbs, actions: isMobile ? undefined : actions });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- breadcrumbs tracked by crumbsKey
    }, [setHeader, title, count, crumbsKey, actions, isMobile]);
    useLayoutEffect(() => () => setHeader({}), [setHeader]);

    return (
        <Stack gap="md" p={{ base: 'xs', sm: 'lg' }} pb={96}>
            {isMobile && (count !== undefined || actions) && (
                <Stack gap="xs">
                    {count !== undefined && (
                        <Text c="dimmed" fz="sm">
                            {count}
                        </Text>
                    )}
                    {actions && (
                        <Group gap="xs" wrap="wrap">
                            {actions}
                        </Group>
                    )}
                </Stack>
            )}
            <Box>{children}</Box>
        </Stack>
    );
};
